import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import { SegmentedPicker, SectionHeader } from './SettingsPrimitives';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';
import { getV2Tables } from '../../../../api/v2Tables';

const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'database', label: 'Database' },
];

export default function DatabaseOptionsSettings({ field, onUpdate, palette, theme }) {
  const source = field.optionsSource || 'manual';
  const [databases, setDatabases] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingDbs, setLoadingDbs] = useState(false);
  const [loadingCols, setLoadingCols] = useState(false);

  // Fetch databases (V1 + V2 merged)
  useEffect(() => {
    if (source !== 'database') return;
    let cancelled = false;
    setLoadingDbs(true);

    getV2Tables()
      .then((v2Res) => {
        if (cancelled) return;
        const list = [];
        if (v2Res?.data) {
          for (const t of v2Res.data) {
            list.push({ id: t.tableId, name: t.tableName || t.tableId, storage: 'V2', columns: t.columns });
          }
        }
        setDatabases(list);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDbs(false); });

    return () => { cancelled = true; };
  }, [source]);

  // Fetch columns when databaseId changes
  useEffect(() => {
    if (source !== 'database' || !field.databaseId) {
      setColumns([]);
      return;
    }
    const selected = databases.find((d) => d.id === field.databaseId);
    if (selected?.columns) {
      setColumns(
        selected.columns.map((c) => ({
          id: c.columnId,
          name: c.columnName || c.columnId,
        }))
      );
    } else {
      setColumns([]);
    }
  }, [source, field.databaseId, databases]);

  const handleSourceChange = useCallback(
    (val) => {
      if (val === 'manual') {
        onUpdate({ optionsSource: 'manual', databaseId: null, labelColumnId: null, valueColumnId: null });
      } else {
        onUpdate({ optionsSource: 'database' });
      }
    },
    [onUpdate]
  );

  const selectedDb = databases.find((d) => d.id === field.databaseId) || null;
  const selectedLabelCol = columns.find((c) => c.id === field.labelColumnId) || null;
  const selectedValueCol = columns.find((c) => c.id === field.valueColumnId) || null;

  const textFieldSx = {
    '& .MuiInputBase-root': {
      fontSize: NODE_UI.fontSize.value,
      borderRadius: NODE_UI.borderRadiusSm,
    },
    '& .MuiOutlinedInput-root': { padding: '2px 8px' },
  };

  const optionSx = { fontSize: NODE_UI.fontSize.value };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
      <SectionHeader label="Options source" palette={palette} />
      <SegmentedPicker
        label=""
        value={source}
        options={SOURCE_OPTIONS}
        onChange={handleSourceChange}
        palette={palette}
      />

      {source === 'database' && (
        <>
          <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary }}>
            Table
          </Typography>
          <Autocomplete
            size="small"
            options={databases}
            value={selectedDb}
            loading={loadingDbs}
            getOptionLabel={(opt) => opt?.name || ''}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            onChange={(_, val) => onUpdate({ databaseId: val?.id || null, labelColumnId: null, valueColumnId: null })}
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <StorageOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography sx={optionSx} noWrap>{opt.name}</Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select table..." variant="outlined" sx={textFieldSx} />
            )}
          />

          {field.databaseId && (
            <>
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary }}>
                Label column
              </Typography>
              <Autocomplete
                size="small"
                options={columns}
                value={selectedLabelCol}
                loading={loadingCols}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                onChange={(_, val) => onUpdate({ labelColumnId: val?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select column..." variant="outlined" sx={textFieldSx} />
                )}
              />

              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, fontWeight: NODE_UI.fontWeight.label, color: palette.textSecondary }}>
                Value column
              </Typography>
              <Autocomplete
                size="small"
                options={columns}
                value={selectedValueCol}
                loading={loadingCols}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                onChange={(_, val) => onUpdate({ valueColumnId: val?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select column..." variant="outlined" sx={textFieldSx} />
                )}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
}
